import { Client } from '@stomp/stompjs';
import { usePrincipal } from '@/common/principal/PrincipalContext';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

/**
 * This context managed Websocket connections with the back end allowing for
 * components to "subscribe" to changes to entities 
 */
const EntityChangeContext = createContext({
  subscrToEntityChange: () => { },
  unsubscrToEntityChange: () => { },
  refreshTrigger: false,
});

export const EntityChangeProvider = ({ children }) => {
  const { getToken, org } = usePrincipal();
  const [refreshTrigger, setRefreshTrigger] = useState(false);

  const [blockRefreshTrigger, setBlockRefreshTrigger] = useState(false);
  const blockRefreshTriggerRef = useRef(blockRefreshTrigger);

  useEffect(() => {
    blockRefreshTriggerRef.current = blockRefreshTrigger;
  }, [blockRefreshTrigger]);

  const toggleBlockRefreshTrigger = useCallback(() => {
    setBlockRefreshTrigger(prev => !prev);
  }, []);

  // Websocket connections must be managed by Module
  // { [module]: { stompClient, connectedPromise, entities: Set<string> } }
  const clientsRef = useRef({});

  /**
 * Exposed function to be used by components to start listening for changes 
 * of an entity in a module
 */
  const subscrToEntityChange = (module, entity, entityIds) => {
    const spec = getStompSpec(module);
    spec.entities.add(entity);

    spec.connectedPromise.then(() => {
      spec.stompClient.publish({
        destination: `/app/${org}/entity-change-subscr`,
        body: JSON.stringify({ entity, entityIds }),
      });
      console.log(`[${module}] subscribed to ${entity}`);
    });
  };

  /**
  * Exposed function to be used by components to stop listening for changes 
  * of an entity in a module
  */
  const unsubscrToEntityChange = (module, entity, entityIds) => {
    const spec = clientsRef.current[module];
    if (!spec) {
      console.warn(`[${module}] no STOMP client to unsubscribe from`);
      return;
    }

    spec.connectedPromise.then(() => {
      spec.stompClient.publish({
        destination: `/app/${org}/entity-change-unsubscr`,
        body: JSON.stringify({ entity, entityIds }),
      });
      console.log(`[${module}] unsubscribed from ${entity}`);
    });

    removeFromCache(module, entity);
  };


  /**
  * Updates the exposed trigger that components can use to refresh data
  */
  const handleEntityChange = (msg) => {
    if (blockRefreshTriggerRef.current) {
      return;
    }

    setRefreshTrigger((prev) => !prev);
  };


  /*
  * This method will create a new Websocket connections for a module
  */
  const createClient = (module) => {

    let resolveConnected;
    const connectedPromise = new Promise((r) => {
      resolveConnected = r;
    });

    const protocol = window.location.protocol.startsWith('https') ? 'wss' : 'ws';
    const host = window.location.hostname === 'localhost' ? 'localhost:8080' : window.location.host;
    const endpoint = `${protocol}://${host}/${module}/api/stomp`;
    const headers = { Authorization: `Bearer ${getToken()}` };

    console.log(`Initializing STOMP for ${module} @ ${endpoint}`);

    const stompClient = new Client({
      brokerURL: endpoint,
      connectHeaders: headers,
      reconnectDelay: 1000,
      debug: (str) => console.log('[STOMP]', str),
      onConnect: () => {
        console.log(`[STOMP:${module}] Connected`);

        // Tunnel for entityChange triggers
        stompClient.subscribe(
          `/user/queue/${org}/entity-change`,
          (frame) => handleEntityChange(JSON.parse(frame.body)),
          headers
        );

        resolveConnected(); // Let others know the connection is ready
      },
      onStompError: (frame) => console.error('[STOMP Error]', frame),
      onWebSocketClose: (evt) => console.log('[WS Closed]', evt),
    });

    stompClient.activate();
    return { stompClient, connectedPromise };

  };


  /*
  * Retrieve or create a stompSpec
  */
  const getStompSpec = (module) => {
    let stompSpec = clientsRef.current[module];
    if (!stompSpec) {
      const { stompClient, connectedPromise } = createClient(module);
      stompSpec = { stompClient, connectedPromise, entities: new Set() };
      clientsRef.current[module] = stompSpec;
    }
    return stompSpec;
  };

  /*
  * Removes an entity from the cache, if no entities left, closes the connection
  */
  const removeFromCache = (module, entity) => {

    const spec = clientsRef.current[module];
    if (!spec) return;

    spec.entities.delete(entity);

    if (spec.entities.size === 0) {
      spec.stompClient.deactivate();
      delete clientsRef.current[module];
    }
  };

  /**
   * In the case of a dismount, cleanup connections
   */
  useEffect(() => {
    return () => {
      Object.values(clientsRef.current).forEach(stompSpec => {
        try {
          stompSpec.stompClient.deactivate();
        } catch (error) {
          console.warn('Error deactivating STOMP client', error);
        }
      });
      // Optionally clear out the ref entirely
      clientsRef.current = {};
      console.log('[EntityChange] All STOMP clients deactivated on unmount');
    };
  }, []);

  return (
    <EntityChangeContext.Provider value={{ subscrToEntityChange, unsubscrToEntityChange, refreshTrigger, blockRefreshTrigger, toggleBlockRefreshTrigger }} >
      {children}
    </EntityChangeContext.Provider>
  );
};

export const useEntityChange = () => useContext(EntityChangeContext);
